import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * PersonPicker — searchable dropdown that merges multiple "people" lists
 * (e.g. Employees, Partners, Suppliers, Customers) into a single typeahead.
 *
 * Stores the chosen value as a plain string by default (`mode='name'`) so it
 * drops into existing forms that expect a free-form name. Pass `mode='id'`
 * + `onChangeRef={(refObj) => …}` if you'd rather get the picked entity's id.
 *
 * Props:
 *   value           – current input value (controlled string)
 *   onChange(name)  – called with the chosen / typed name
 *   onPick(person)  – optional — called with the full {id,name,role,type}
 *                     object whenever an option is clicked (vs free-form typed)
 *   sources         – array of { type, label, items, badgeClass } where items
 *                     is an array of {id, name, role|position|title}.
 *                     Defaults to a 2-source [employees, partners] when the
 *                     legacy `employees`/`partners` props are passed instead.
 *   employees       – legacy convenience prop (used when `sources` not given)
 *   partners        – legacy convenience prop (used when `sources` not given)
 *   placeholder     – input placeholder
 *   testId          – data-testid prefix; suffixes: -input, -toggle, -search,
 *                     -option-{id}
 *   disabled        – disables both the input and the toggle button
 *   maxOptions      – cap the visible list (default 50)
 */
const TYPE_BADGES = {
  employee: 'bg-info/10 text-info border-info/30',
  partner: 'bg-violet-50 text-violet-700 border-violet-200',
  supplier: 'bg-amber-50 text-amber-700 border-amber-200',
  customer: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  beneficiary: 'bg-pink-50 text-pink-700 border-pink-200',
};

const TYPE_LABELS = {
  employee: 'Employee', partner: 'Partner', supplier: 'Supplier',
  customer: 'Customer', beneficiary: 'Beneficiary',
};

export function PersonPicker({
  value = '',
  onChange,
  onPick,
  sources,
  employees,
  partners,
  placeholder = 'Pick a person or type a name',
  testId = 'person-picker',
  disabled = false,
  maxOptions = 50,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  // Close when user clicks outside the wrapper. Document-level listener so
  // the input stays interactive while the dropdown is open.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Build the merged list. Either `sources` (preferred, generic) or the
  // legacy `employees`/`partners` props are accepted.
  const allPeople = (() => {
    if (Array.isArray(sources) && sources.length > 0) {
      return sources.flatMap(src =>
        (src.items || []).map(p => ({
          id: p.id,
          name: p.name || p.full_name,
          role: p.role || p.position || p.title || p.tax_number,
          type: src.type,
          badgeLabel: src.label || TYPE_LABELS[src.type] || src.type,
          badgeClass: src.badgeClass || TYPE_BADGES[src.type] || 'bg-muted text-muted-foreground border-border',
        })),
      ).filter(p => p.name);
    }
    return [
      ...(employees || []).map(e => ({
        id: e.id, name: e.name || e.full_name, role: e.role || e.position, type: 'employee',
        badgeLabel: 'Employee', badgeClass: TYPE_BADGES.employee,
      })),
      ...(partners || []).map(p => ({
        id: p.id, name: p.name, role: p.role || p.title, type: 'partner',
        badgeLabel: 'Partner', badgeClass: TYPE_BADGES.partner,
      })),
    ].filter(p => p.name);
  })();

  const q = query.trim().toLowerCase();
  const filtered = q
    ? allPeople.filter(p =>
        (p.name || '').toLowerCase().includes(q)
        || (p.role || '').toLowerCase().includes(q),
      )
    : allPeople;

  const select = (p) => {
    onChange?.(p.name);
    onPick?.(p);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={testId}>
      <div className="flex gap-1">
        <Input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1"
          disabled={disabled}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="px-2"
          onClick={() => setOpen(o => !o)}
          disabled={disabled}
          data-testid={`${testId}-toggle`}
          title="Pick from list"
        >
          <ChevronDown size={14} />
        </Button>
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-[280px] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border bg-muted/30 flex items-center gap-2">
            <Search size={13} className="text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid={`${testId}-search`}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="p-0.5 hover:bg-muted rounded" aria-label="Clear search">
                <X size={12} className="text-muted-foreground" />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="p-4 text-center text-xs text-muted-foreground">
                No matches. You can still type a free-form name above.
              </p>
            ) : (
              filtered.slice(0, maxOptions).map(p => (
                <button
                  key={`${p.type}-${p.id}`}
                  type="button"
                  onClick={() => select(p)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary/10 transition-colors border-b border-border/40 last:border-0"
                  data-testid={`${testId}-option-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">{p.name}</div>
                    {p.role && <div className="text-[10px] text-muted-foreground truncate">{p.role}</div>}
                  </div>
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${p.badgeClass}`}>
                    {p.badgeLabel}
                  </Badge>
                </button>
              ))
            )}
            {filtered.length > maxOptions && (
              <p className="p-2 text-center text-[10px] text-muted-foreground border-t">
                Showing first {maxOptions} of {filtered.length} — refine search to narrow down.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonPicker;
